import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const read = (k, f) => { try { return JSON.parse(localStorage.getItem(k) || 'null') ?? f; } catch { return f; } };
export const reduced = () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// All Home data (performance summary, activity ticker, action queue) with last-known cache,
// 30s refresh and optimistic complete / snooze / undo. Every figure comes from the workspace APIs.
const TINTS = ['blue', 'amber', 'sky', 'violet', 'slate', 'rose', 'teal'];
const tintFor = (key) => TINTS[[...String(key)].reduce((n, c) => n + c.charCodeAt(0), 0) % TINTS.length];

export const useHomeData = ({ token, userId, period, me }) => {
  const H = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const key = `dwh_${userId || 'u'}`;
  const [summary, setSummary] = useState(() => read(`${key}_summary`, {}));
  const [activity, setActivity] = useState(() => read(`${key}_activity`, []));
  const [queue, setQueue] = useState(() => read(`${key}_queue`, { items: [], cleared_today: 0, total_today: 0, more: 0 }));
  const [syncedAt, setSyncedAt] = useState(() => Date.now());
  const [toasts, setToasts] = useState([]);
  const pending = useRef(new Map());
  const seenDone = useRef(null);
  const [freshId, setFreshId] = useState(null);

  const toast = useCallback((msg, undo, ic = 'check') => {
    const id = `${Date.now()}_${Math.random()}`;
    const ms = undo ? 5000 : 2600;
    setToasts((t) => [...t, { id, msg, undo, ic, ms }].slice(-3));
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), ms);
    return id;
  }, []);
  const killToast = useCallback((id) => setToasts((t) => t.filter((x) => x.id !== id)), []);

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const [s, a, q] = await Promise.all([
        axios.get(`${API}/api/home/summary?period=${period}`, H),
        axios.get(`${API}/api/home/activity?limit=20`, H),
        axios.get(`${API}/api/home/queue`, H),
      ]);
      setSummary((p) => { const n = { ...p, [period]: s.data }; localStorage.setItem(`${key}_summary`, JSON.stringify(n)); return n; });
      setActivity(a.data.items || []); localStorage.setItem(`${key}_activity`, JSON.stringify(a.data.items || []));
      // Keep optimistic removals while a request is in flight.
      const qd = { ...q.data, items: (q.data.items || []).filter((i) => !pending.current.has(i.id)) };
      // Announce client completions that arrived since the last poll (not on first load).
      const doneNow = qd.items.filter((i) => i.attention);
      if (seenDone.current) {
        const fresh = doneNow.filter((i) => !seenDone.current.has(i.id) && !i.acknowledged);
        if (fresh.length) { setFreshId(fresh[0].id); fresh.slice(0, 2).forEach((i) => toast(`${i.name}: ${i.tag.toLowerCase()}`, null, 'userok')); }
      }
      seenDone.current = new Set(doneNow.map((i) => i.id));
      setQueue(qd); localStorage.setItem(`${key}_queue`, JSON.stringify(qd));
      setSyncedAt(Date.now());
    } catch { /* keep last-known */ }
  }, [token, period, H, key, toast]);

  // Live: poll every 15s while visible, and refresh as soon as the tab regains focus.
  useEffect(() => {
    refresh();
    const t = setInterval(() => { if (!document.hidden) refresh(); }, 15000);
    const onVis = () => { if (!document.hidden) refresh(); };
    document.addEventListener('visibilitychange', onVis); window.addEventListener('focus', onVis);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVis); window.removeEventListener('focus', onVis); };
  }, [refresh]);
  useEffect(() => {
    if (!token || summary.week) return;
    axios.get(`${API}/api/home/summary?period=week`, H).then((r) => setSummary((p) => ({ ...p, week: r.data }))).catch(() => {});
  }, [token, H, summary.week]);

  const removeLocal = useCallback((id, cleared) => setQueue((q) => {
    const items = q.items.filter((i) => i.id !== id);
    return { ...q, items, cleared_today: q.cleared_today + (cleared ? 1 : 0), total_today: cleared ? q.total_today : q.total_today - 1 };
  }), []);
  const restoreLocal = useCallback((item, idx, cleared) => setQueue((q) => {
    const items = [...q.items]; items.splice(Math.min(idx, items.length), 0, item);
    return { ...q, items, cleared_today: q.cleared_today - (cleared ? 1 : 0), total_today: cleared ? q.total_today : q.total_today + 1 };
  }), []);

  // Optimistic: remove now, call API, offer Undo (which reverses the real action too).
  const complete = useCallback(async (item, msg) => {
    const idx = queue.items.findIndex((i) => i.id === item.id);
    pending.current.set(item.id, true); removeLocal(item.id, true);
    let failed = false;
    try { await axios.post(`${API}/api/home/queue/${encodeURIComponent(item.id)}/complete`, { sig: item.sig, name: item.name, type: item.type, primary: item.primary }, H); }
    catch { failed = true; }
    if (failed) { pending.current.delete(item.id); restoreLocal(item, idx, true); toast(`Couldn't complete ${item.name}. Try again.`, null, 'x'); return; }
    toast(msg || (item.primary === 'Done' ? `${item.name} done` : `${item.name}: ${item.primary.toLowerCase()} done`), async () => {
      restoreLocal(item, idx, true);
      try { await axios.post(`${API}/api/home/queue/${encodeURIComponent(item.id)}/undo`, {}, H); } catch { /* refresh will reconcile */ }
      pending.current.delete(item.id); refresh();
    });
    setTimeout(() => pending.current.delete(item.id), 6000);
  }, [queue.items, H, removeLocal, restoreLocal, toast, refresh]);

  const snooze = useCallback(async (item) => {
    const idx = queue.items.findIndex((i) => i.id === item.id);
    pending.current.set(item.id, true); removeLocal(item.id, false);
    let label = 'next working day 9am';
    try { const r = await axios.post(`${API}/api/home/queue/${encodeURIComponent(item.id)}/snooze`, {}, H); label = r.data.label || label; }
    catch { pending.current.delete(item.id); restoreLocal(item, idx, false); toast(`Couldn't snooze ${item.name}.`, null, 'x'); return; }
    toast(`${item.name} snoozed until ${label}`, async () => {
      restoreLocal(item, idx, false);
      try { await axios.post(`${API}/api/home/queue/${encodeURIComponent(item.id)}/undo`, {}, H); } catch { /* ignore */ }
      pending.current.delete(item.id); refresh();
    }, 'clock');
    setTimeout(() => pending.current.delete(item.id), 6000);
  }, [queue.items, H, removeLocal, restoreLocal, toast, refresh]);

  const send = useCallback(async (item, draft) => {
    const idx = queue.items.findIndex((i) => i.id === item.id);
    pending.current.set(item.id, true); removeLocal(item.id, true);
    try {
      const r = await axios.post(`${API}/api/home/queue/${encodeURIComponent(item.id)}/send`, draft, H);
      const e = r.data.email || {};
      const note = e.ok && !e.dry_run ? `Sent to ${draft.to}.` : e.reason === 'blocked_preview' ? 'Email held (preview environment).' : e.reason === 'no_address' ? 'Draft saved (no email address).' : 'Draft saved.';
      toast(`${note} ${item.name} cleared`, async () => {
        restoreLocal(item, idx, true);
        try { await axios.post(`${API}/api/home/queue/${encodeURIComponent(item.id)}/undo`, {}, H); } catch { /* ignore */ }
        pending.current.delete(item.id); refresh();
      }, 'send');
    } catch { pending.current.delete(item.id); restoreLocal(item, idx, true); toast(`Couldn't send for ${item.name}.`, null, 'x'); }
    setTimeout(() => pending.current.delete(item.id), 6000);
  }, [queue.items, H, removeLocal, restoreLocal, toast, refresh]);

  // Acknowledge a client completion: stops the pulse; the item stays until its next action is done.
  const ack = useCallback(async (item) => {
    if (!item.attention || item.acknowledged) return;
    const set = (v) => setQueue((q) => ({ ...q, items: q.items.map((i) => (i.id === item.id ? { ...i, acknowledged: v } : i)) }));
    set(true);
    try { await axios.post(`${API}/api/home/queue/${encodeURIComponent(item.id)}/ack`, {}, H); } catch { set(false); }
  }, [H]);

  const addTask = useCallback(async (client, what) => {
    const r = await axios.post(`${API}/api/home/attention/tasks`, { title: `${client}: ${what}`, priority: 'medium' }, H);
    const t = r.data;
    // Same shape the server builds in home_queue._task_item so the row doesn't change on refresh.
    const item = { id: t.id, name: client, initials: client.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase(), tint: tintFor(t.id), type: 'task', tag: 'Task',
      meta: 'Added by you', when: '', why: 'New', hot: false, next: what, primary: 'Done', draft: { to: client, subject: what, body: `Hi ${client.split(' ')[0]},\n\n\n\nCheers,\n${me || ''}` } };
    setQueue((q) => ({ ...q, items: [item, ...q.items], total_today: q.total_today + 1 }));
    toast(`Task added for ${client}`, null, 'plus');
    return item;
  }, [H, toast, me]);

  return { summary, activity, queue, syncedAt, refresh, complete, snooze, send, addTask, ack, freshId, toasts, toast, killToast };
};

// Number "decode" effect from the prototype — digits scramble into place over ~900ms.
export const useDecode = (final) => {
  const [out, setOut] = useState(final);
  useEffect(() => {
    if (final == null || reduced() || !/\d/.test(final)) { setOut(final); return undefined; }
    const dur = 900; const start = performance.now(); let raf;
    const step = (t) => {
      const p = Math.min(1, (t - start) / dur); const settled = Math.floor(p * final.length);
      setOut(final.split('').map((ch, i) => (i < settled || !/\d/.test(ch) ? ch : String(Math.floor(Math.random() * 10)))).join(''));
      if (p < 1) raf = requestAnimationFrame(step); else setOut(final);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [final]);
  return out;
};

export const money = (v) => (v == null ? '–' : `$${Number(v).toLocaleString('en-AU')}`);
export const num = (v) => (v == null ? '–' : Number(v).toLocaleString('en-AU'));
export const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
export const MOD = isMac ? '⌘' : 'Ctrl';
